import { styled } from '@mui/material/styles';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Paper, Typography } from '@mui/material';
import { TripLegType } from '../../../data/models/reservation.model';
import { AlternateContactType } from '../../../data/models/alternate-contact-type.model';
import {
  TripLegPassengerNoteType,
  TripLegDriverNoteType,
} from '../../../data/models/note.model';
import { useEffect, useMemo, useState } from 'react';
import {
  TransportationAttribute,
  TransportationAttributeGroup,
} from '../../../data/models/transportation-attribute.model';
import MemberForm from './MemberForm';
import PassengerBenefits from '../passengers/PassengerBenefits';
import { ServicePlan } from '../../../data/models/service-plan.model';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import BookTripSupport from '../../member/reservation/BookTripSupport';
import { usePortalName } from '../../../hooks/usePortalName';

const PREFIX = 'ReservationMemberForm';

const classes = {
  container: `${PREFIX}-container`,
  previous: `${PREFIX}-previous`,
  title: `${PREFIX}-title`,
  form: `${PREFIX}-form`,
  benefits: `${PREFIX}-benefits`,
  actions: `${PREFIX}-actions`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },

  [`& .${classes.previous}`]: {
    display: 'flex',
    alignItems: 'end',
    textDecoration: 'none',
    textTransform: 'uppercase',
    fontSize: 14,
    fontWeight: 600,
    fontStyle: 'normal',
    letterSpacing: 1.5,
    lineHeight: '157%',
    marginLeft: theme.spacing(-1),
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    flex: 1,
    width: '-webkit-fill-available',
  },

  [`& .${classes.benefits}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 550,
  },

  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(2),
  },
}));

enum AttributeControlType {
  CHECKBOX,
  DROPDOWN,
}

interface SecondaryAttribute {
  id: string;
  name: string;
  controlType: AttributeControlType;
  listOptions?: TransportationAttribute[];
}

interface ReservationMemberFormProps {
  stepCount: number;
  tripCount: number;
  tripType: TripLegType;
  transportationAttributes: TransportationAttribute[];
  passengerNoteTypes: TripLegPassengerNoteType[];
  driverNoteTypes: TripLegDriverNoteType[];
  alternateContactTypes: AlternateContactType[];
  servicePlans: ServicePlan[];
  formHook: UseFormReturn<any, any>;
  isSaving: boolean;
  isValid: boolean;
  allowOverride: boolean;
  isOverridden: boolean;
  canSaveAsDraft: boolean;
  isPortalReservation: boolean;
  goBack: (evt: any) => void;
  next: () => void;
  override: () => void;
  saveAsDraft: () => void;
}

export default function ReservationMemberForm({
  stepCount,
  tripCount,
  tripType,
  transportationAttributes,
  passengerNoteTypes,
  driverNoteTypes,
  alternateContactTypes,
  formHook,
  servicePlans,
  isSaving,
  isValid,
  allowOverride,
  isOverridden,
  canSaveAsDraft,
  isPortalReservation,
  goBack,
  next,
  override,
  saveAsDraft,
}: ReservationMemberFormProps) {
  const { t } = useTranslation('common');

  // isPortal is required to determine if the reservation is being made through a portal or not,
  // beacuse isPortalReservation is manually set to true / false based on the origin of the reservation dialog.
  const isPortal = usePortalName() ? true : false;
  const requiredFieldMessage = t('common.requiredField');

  const emptyAttribute: TransportationAttribute = useMemo(
    () => ({
      transportationAttributeCategoryBC: '',
      name: t('reservation.noAttribute'),
      description: '',
      isPassengerSelectable: true,
      isDefault: false,
    }),
    [t],
  );

  const [secondaryAttributes, setSecondaryAttributes] = useState<
    SecondaryAttribute[]
  >([]);

  useEffect(() => {
    if (transportationAttributes) {
      const otherAttributes: SecondaryAttribute[] = [];
      const secondary = transportationAttributes.filter(
        (a) =>
          a.grouping === TransportationAttributeGroup.SECONDARY &&
          a.isPassengerSelectable,
      );
      secondary.forEach((s) => {
        let options: TransportationAttribute[] | undefined = undefined;
        if (s.childTransportationAttributeCategoryBC) {
          const children = transportationAttributes.filter(
            (ta) =>
              ta.transportationAttributeCategoryBC ===
              s.childTransportationAttributeCategoryBC,
          );
          if (children?.length) {
            options = children.sort((c1, c2) => c1.name.localeCompare(c2.name));
          }
        }
        otherAttributes.push({
          id: s.transportationAttributeUUID!,
          name: s.name,
          controlType: options
            ? AttributeControlType.DROPDOWN
            : AttributeControlType.CHECKBOX,
          listOptions: options,
        });
      });
      setSecondaryAttributes(otherAttributes);
    }
  }, [transportationAttributes]);

  const tripLegs = Array(tripCount)
    .fill(0)
    .map((_, step) => {
      const pickupAddress = formHook.watch(
        `MultiLegTrips[${step}].PickupAddressStruct`,
      );
      const dropoffAddress = formHook.watch(
        `MultiLegTrips[${step}].DropoffAddressStruct`,
      );
      const pickupTimeZone = formHook.watch(
        `MultiLegTrips[${step}].PickupTimeZone`,
      );

      return {
        pickupAddress: pickupAddress ? JSON.parse(pickupAddress) : {},
        dropoffAddress: dropoffAddress ? JSON.parse(dropoffAddress) : {},
        pickupTime: formHook.watch(`MultiLegTrips[${step}].PickupTime`),
        pickupTimeZone: pickupTimeZone,
        appointmentDate: formHook.watch(
          `MultiLegTrips[${step}].AppointmentTime`,
        ),
      };
    });

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.form}>
        <Link
          color="primary"
          className={classes.previous}
          href="#"
          onClick={goBack}
          underline="hover">
          <ChevronLeftOutlinedIcon />
          {t('common.back')}
        </Link>
        <Typography className={classes.title}>
          {t('reservation.memberDetails')}
        </Typography>
        <MemberForm
          transportationAttributes={transportationAttributes}
          passengerNoteTypes={passengerNoteTypes}
          driverNoteTypes={driverNoteTypes}
          alternateContactTypes={alternateContactTypes}
          formHook={formHook}
          isPortalReservation={isPortal ? true : isPortalReservation}
        />
        <Box className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            disabled={!isValid}
            onClick={next}
            data-fs-element="CreateReservationStep2NextButton">
            {t('reservation.next')}
          </Button>
          {!isPortal && (
            <>
              <Button
                color="secondary"
                variant="contained"
                disabled={isSaving || !canSaveAsDraft}
                onClick={saveAsDraft}
                data-fs-element="CreateReservationSaveAsDraftButton">
                {t('reservation.saveAsDraft')}
              </Button>
              <Button
                color="secondary"
                variant="contained"
                disabled={isSaving || !allowOverride || isOverridden}
                onClick={override}
                data-fs-element="CreateReservationRequestOverrideStartButton">
                {t('reservation.requestOverride')}
              </Button>
            </>
          )}
        </Box>
      </Paper>
      {!isPortalReservation && (
        <Box className={classes.benefits}>
          <PassengerBenefits
            appointmentDateTime={tripLegs[0].appointmentDate}
            servicePlans={servicePlans}
          />
        </Box>
      )}
      {isPortalReservation && <BookTripSupport />}
    </StyledBox>
  );
}
